import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiHomeGetInput,
  type EnrollmentMultiHomeData,
  type EnrollmentMultiHomeSubmitInput,
  type EnrollmentMultiHomeSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'
import { getEnrollmentMultiHomeDataQuery } from './queries'

// Re-export query for backward compatibility
export { getEnrollmentMultiHomeDataQuery }

/**
 * Get Enrollment Multi Home data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiHomeData>
 * @example const response = await getEnrollmentMultiHomeData()
 */
export const getEnrollmentMultiHomeData = async (
  enrollmentMultiHomeGetInput?: EnrollmentMultiHomeGetInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiHomeData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiHomeData',
      query: getEnrollmentMultiHomeDataQuery,
    },
    {
      submitEnrollmentInput: enrollmentMultiHomeGetInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiHomeData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiHomeData = createQueryHook<EnrollmentMultiHomeSubmitInput, EnrollmentMultiHomeData>(
  getEnrollmentMultiHomeData,
)

/**
 * Submit Enrollment Multi Home data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiHomeSubmitOutput>
 * @example const response = await submitEnrollmentMultiHomeData();
 */
export const submitEnrollmentMultiHomeData = async (
  enrollmentHomeSubmitInput: EnrollmentMultiHomeSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiHomeSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiHomeData',
      query: `mutation submitEnrollmentMultiHomeData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentHomeSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiHomeSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiHomeData = createMutationHook<
  EnrollmentMultiHomeSubmitInput,
  EnrollmentMultiHomeSubmitOutput
>(submitEnrollmentMultiHomeData)
